/* TrustBadge — anti-hallucinatie verificatiebadge + "Verifieer nu"-knop.
 *
 * Toont de trust-score van een signaal of kandidaat:
 *   ≥70 groen (geverifieerd) · 40-69 amber (deels) · <40 rood (verdacht)
 *   geen verificatie → grijze "Niet geverifieerd" met verify-knop.
 * De tooltip bevat het volledige check-rapport (✓/✗ per check).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2 } from "lucide-react";

const STYLE = {
    verified: { cls: "bg-[#22c55e]/12 text-[#22c55e] border-[#22c55e]/40", Icon: ShieldCheck },
    partial:  { cls: "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)] border-[var(--jr-amber)]/40", Icon: ShieldAlert },
    suspect:  { cls: "bg-[var(--jr-error)]/12 text-[var(--jr-error)] border-[var(--jr-error)]/40", Icon: ShieldAlert },
};

export function checksTooltip(ver) {
    if (!ver?.checks) return "";
    return ver.checks.map((c) => `${c.ok ? "✓" : "✗"} ${c.detail}`).join("\n");
}

/**
 * entity: "signal" | "candidate"; entityId: doc id
 * verification: bestaande verificatie-dict (of null)
 * onVerified: callback(ver) na succesvolle (her)verificatie
 */
export function TrustBadge({ entity, entityId, verification, onVerified, testid }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);

    const verify = async (e) => {
        e?.stopPropagation?.(); e?.preventDefault?.();
        setBusy(true);
        try {
            const r = await api.post(`/verify/${entity}/${entityId}`);
            toast.success(t("verify.verified_toast", { score: r.data.trust_score }));
            onVerified?.(r.data);
        } catch (err) { toast.error(err?.response?.data?.detail || t("verify.failed")); }
        finally { setBusy(false); }
    };

    if (!verification) {
        return (
            <button onClick={verify} disabled={busy} data-testid={testid || `verify-${entity}-${entityId}`}
                title={t("verify.verify_now_tip")}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded border border-white/15 bg-white/[0.04] text-[var(--jr-text-3)] hover:text-white hover:border-white/30 transition-colors">
                {busy ? <Loader2 size={10} className="animate-spin"/> : <ShieldQuestion size={10}/>}
                {busy ? t("verify.verifying") : t("verify.verify_now")}
            </button>
        );
    }
    const s = STYLE[verification.status] || STYLE.suspect;
    const Icon = s.Icon;
    return (
        <button onClick={verify} disabled={busy} data-testid={testid || `trust-${entity}-${entityId}`}
            title={`${t("verify.reverify_tip")}\n\n${checksTooltip(verification)}`}
            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded border cursor-pointer hover:brightness-125 transition-all ${s.cls}`}>
            {busy ? <Loader2 size={10} className="animate-spin"/> : <Icon size={10}/>}
            {t("verify.trust")} {verification.trust_score} · {t(`verify.badge_${verification.status}`)}
        </button>
    );
}
